import type { BetSelection } from "@/features/betslip/types";

export type ValidationError = {
  outcomeId: string;
  reason: "no-stake" | "market-inactive" | "unaccepted-odds";
};

export type ValidationResult = {
  valid: boolean;
  errors: ValidationError[];
};

/**
 * Why a slip can't be submitted as it stands, per selection. `bettable` carries
 * both reasons a market is off: dead status, or live without the permission.
 * Leave it out to skip that check entirely - an empty set blocks everything.
 */
export const validateSelections = (
  selections: Record<string, BetSelection>,
  stakes: Record<string, number>,
  oddsChanges?: Partial<Record<string, number>>,
  bettable?: Readonly<Record<string, boolean>>,
): ValidationResult => {
  const errors: ValidationError[] = [];

  for (const outcomeId of Object.keys(selections)) {
    const stake = stakes[outcomeId] ?? 0;
    if (stake <= 0) {
      errors.push({ outcomeId, reason: "no-stake" });
    }

    if (bettable && !bettable[outcomeId]) {
      errors.push({ outcomeId, reason: "market-inactive" });
    }

    if (oddsChanges?.[outcomeId] !== undefined) {
      errors.push({ outcomeId, reason: "unaccepted-odds" });
    }
  }

  return { valid: errors.length === 0, errors };
};

export const legSetKey = (outcomeIds: readonly string[]): string =>
  [...outcomeIds].sort().join("|");

/**
 * Does any SEM or exotic in the slip have exactly this set of legs?
 *
 * Quinella (RUNNER_TOP_2) duplicates every runner into positions 1 and 2, so
 * exotic positions are deduped before keying or the set never matches the
 * builder's unique outcome ids.
 */
export const hasSelectionWithLegSet = (
  selections: Record<string, BetSelection>,
  outcomeIds: readonly string[],
): boolean => {
  const targetKey = legSetKey(outcomeIds);

  for (const selection of Object.values(selections)) {
    if (selection.kind === "sem") {
      if (legSetKey(selection.legs.map((leg) => leg.outcomeId)) === targetKey) {
        return true;
      }
    } else if (selection.kind === "exotic") {
      const ids = new Set(
        selection.positions.flatMap((position) =>
          position.map((outcome) => outcome.outcomeId),
        ),
      );
      if (legSetKey([...ids]) === targetKey) return true;
    }
  }

  return false;
};

export const isDuplicateBetslip = (
  currentOutcomeIds: string[],
  previousOutcomeIds: string[] | null | undefined,
): boolean => {
  if (!previousOutcomeIds) return false;
  if (currentOutcomeIds.length !== previousOutcomeIds.length) return false;

  const sorted = [...currentOutcomeIds].sort();
  const prevSorted = [...previousOutcomeIds].sort();
  return sorted.every((id, i) => id === prevSorted[i]);
};

const competitorIdOf = (selection: BetSelection): string | undefined => {
  if (
    selection.kind === "match" ||
    selection.kind === "outright" ||
    selection.kind === "race"
  ) {
    return selection.competitor?.id;
  }
  return undefined;
};

const competitorNamesOf = (selection: BetSelection): string[] => {
  if (selection.kind === "sem") {
    return Object.values(selection.competitors)
      .map((competitor) => competitor.name)
      .filter((name): name is string => typeof name === "string");
  }
  // Single selection: competitor lifted to the selection itself.
  if (
    selection.kind === "match" ||
    selection.kind === "outright" ||
    selection.kind === "race"
  ) {
    const name = selection.competitor?.name;
    return name ? [name] : [];
  }
  return [];
};

/**
 * Two selections conflict if at least one of these is true:
 * - either is non-multiable (and not the special independent-per-event case)
 * - they share a competitorId (e.g. both pick the same player to win)
 * - both are FIFA and share a competitor name (PKB-2822)
 * - one is exotic (exotic + anything else can't form a multi)
 * - cross-product racing × sports without the env flag enabled
 * - one explicitly relates to the other via relatedEventIds
 * - they're both on the same eventId
 *
 * Order-independent: caller checks both directions.
 */
export const areRelated = (
  selectionA: BetSelection,
  selectionB: BetSelection,
  options: { allowCrossProduct?: boolean } = {},
): boolean => {
  if (selectionA.outcomeId === selectionB.outcomeId) return false;

  // independentPerEvent is the explicit opt-out — markets like "to score
  // anytime" are allowed alongside the same event's win market.
  if (selectionA.independentPerEvent || selectionB.independentPerEvent) {
    // Still need to check cross-event conflicts, but independent-per-event
    // bypasses same-event and same-competitor checks.
  } else {
    if (!selectionA.isMultiable || !selectionB.isMultiable) return true;

    const competitorIdA = competitorIdOf(selectionA);
    const competitorIdB = competitorIdOf(selectionB);
    if (competitorIdA && competitorIdB && competitorIdA === competitorIdB) {
      return true;
    }

    if (selectionA.sport === "FIFA" && selectionB.sport === "FIFA") {
      const namesA = competitorNamesOf(selectionA);
      const namesB = competitorNamesOf(selectionB);
      if (namesA.some((name) => namesB.includes(name))) return true;
    }
  }

  if (selectionA.kind === "exotic" || selectionB.kind === "exotic") return true;

  if (!options.allowCrossProduct) {
    const aIsRacing = selectionA.hub === "racing";
    const bIsRacing = selectionB.hub === "racing";
    if (aIsRacing !== bIsRacing) return true;
  }

  if (selectionA.relatedEventIds?.[selectionB.eventId]) return true;
  if (selectionB.relatedEventIds?.[selectionA.eventId]) return true;

  if (selectionA.eventId === selectionB.eventId) return true;

  return false;
};
